import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from 'common-src/components/base/Button';
import { cachePortalInvites } from 'common-src/features/cache/actions';
import { apiRequest } from 'common-src/features/rest';
import { getPatientRestRequest } from 'common-src/models/Patient';
import { getPatientCaregiversRequest } from 'common-src/models/PatientCaregiver';

import { ModalType } from 'src/components/base/ModalGroup';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';

import { TestType } from './constants';
import styles from './PatientPortalAccess.module.scss';

const PatientPortalAccess = ({ patientId, portalPatientId }) => {
  const dispatch = useDispatch();
  const { showAlert, AlertType } = useAlert();
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateMemberPortalAccess = async () => {
    setIsCreating(true);
    try {
      const response = await apiRequest({
        endpoint: 'patientPortalBackend/createMemberPortalAccess',
        method: 'POST',
        body: {
          patient_id: patientId,
        },
      });

      const data = await response.json();
      showAlert(
        AlertType.Success,
        '',
        data.message || 'Member portal access created successfully.',
      );
      dispatch(getPatientRestRequest(patientId));
      dispatch(cachePortalInvites(portalPatientId, null));
      dispatch(getPatientCaregiversRequest({ patientId }));
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Failed to create member portal access.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDistributeResults = async (results) => {
    try {
      const requests = results.map((result) =>
        apiRequest({
          endpoint: 'patientPortalBackend/createAndSubmitAssessment',
          method: 'POST',
          body: {
            patient_id: patientId,
            test_type: TestType[result],
          },
        }).then(async (response) => {
          const data = await response.json();
          if (response.ok) {
            showAlert(AlertType.Success, '', data.message || `${result} distributed successfully.`);
            dispatch(cachePortalInvites(portalPatientId, null));
          } else {
            showAlert(AlertType.Error, '', data.detail || `Failed to distribute ${result}.`);
          }
        }),
      );
      await Promise.all(requests);
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Failed to distribute results.');
    }
  };

  const handleDistributeClick = () => {
    dispatch(
      openModal(ModalType.DISTRIBUTE_RESULTS, {
        onSubmit: handleDistributeResults,
      }),
    );
  };

  return (
    <div className={styles.container}>
      <Button
        text="Create Member Portal Access"
        onClick={handleCreateMemberPortalAccess}
        classNames={[styles.button]}
        disabled={isCreating}
      />
      <Button
        text="Distribute Results to Member Portal"
        onClick={handleDistributeClick}
        classNames={[styles.button]}
      />
    </div>
  );
};

PatientPortalAccess.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  portalPatientId: PropTypes.string,
};

export default PatientPortalAccess;
